/**
 * PaymentStatus.jsx — Payment result page for AOS-PAY-UNIFIED-01
 *
 * Route: /campus/pay/status/:intentId
 *
 * Shows: success/failure/pending state, receipt details, back navigation.
 * Simulator receipts are clearly labeled per INV-PAY-SEC-05.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Receipt,
  ArrowLeft,
  Download,
  AlertTriangle,
  Home,
  ChevronRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  return localStorage.getItem("academiaos_token");
}

function authHeaders() {
  return { Authorization: `Bearer ${getToken()}` };
}

function fmtAmount(amount, currency = "INR") {
  const n = parseFloat(amount);
  if (isNaN(n)) return "₹—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "INR",
    minimumFractionDigits: 2,
  }).format(n);
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function PaymentStatus() {
  const { intentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const fm3PaymentId = searchParams.get("fm3_payment_id");
  const contextLabel = searchParams.get("context_label") || "Payment";

  const [intent, setIntent] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleDownloadPdf = async () => {
    if (!intentId) return;
    setPdfLoading(true);
    try {
      const response = await axios.get(`${API}/api/pay/receipt/${intentId}/pdf`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      const rnum = receipt?.receipt_number || intent?.receipt_number || intentId.slice(0, 8);
      link.setAttribute("download", `receipt-${rnum}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Receipt PDF downloaded");
    } catch (e) {
      toast.error("Failed to download PDF receipt");
    } finally {
      setPdfLoading(false);
    }
  };

  const loadIntent = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/pay/intent/${intentId}`, {
        headers: authHeaders(),
      });
      setIntent(r.data);
      return r.data;
    } catch (e) {
      toast.error("Failed to load payment status");
      return null;
    }
  }, [intentId]);

  const loadReceipt = useCallback(
    async (paymentIdOrIntentId) => {
      if (!paymentIdOrIntentId) return;
      try {
        // Try intent-based receipt first (works for both hostel FM3 and admissions)
        const r = await axios.get(`${API}/api/pay/intent/${intentId}/receipt`, {
          headers: authHeaders(),
        });
        setReceipt(r.data);
      } catch (_) {
        // Fall back to payment_id based receipt
        if (paymentIdOrIntentId !== intentId) {
          try {
            const r = await axios.get(`${API}/api/pay/receipt/${paymentIdOrIntentId}`, {
              headers: authHeaders(),
            });
            setReceipt(r.data);
          } catch (__) {}
        }
      }
    },
    [intentId]
  );

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(true);
      const data = await loadIntent();
      if (mounted && data) {
        const pid = fm3PaymentId || data?.fm3_payment_id;
        if (pid) await loadReceipt(pid);
      }
      setLoading(false);
    };
    run();
    return () => {
      mounted = false;
    };
  }, [loadIntent, loadReceipt, fm3PaymentId]);

  // Poll if still pending
  useEffect(() => {
    if (!intent || intent.status === "SUCCEEDED" || intent.status === "FAILED") return;
    setPolling(true);
    const iv = setInterval(async () => {
      const data = await loadIntent();
      if (!data) return;
      if (data.status === "SUCCEEDED" || data.status === "FAILED") {
        clearInterval(iv);
        setPolling(false);
        const pid = data.fm3_payment_id;
        if (pid) await loadReceipt(pid);
      }
    }, 3000);
    return () => {
      clearInterval(iv);
      setPolling(false);
    };
  }, [intent?.status, loadIntent, loadReceipt]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw size={28} className="animate-spin text-blue-500" />
      </div>
    );
  }

  const status = intent?.status || "PENDING";
  const isSuccess = status === "SUCCEEDED";
  const isFail = status === "FAILED" || status === "CANCELLED" || status === "EXPIRED";
  const isPending = !isSuccess && !isFail;
  const isSimulated =
    intent?.provider &&
    (intent.provider === "simulator" || intent.provider === "simulator_upi");

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/20 p-4 sm:p-6"
      data-testid="payment-status-page"
    >
      <div className="mx-auto max-w-md space-y-4">
        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft size={14} />
          Back
        </button>

        {/* Status card */}
        <div className="rounded-2xl bg-white border border-slate-200 shadow-lg overflow-hidden">
          {/* Status header */}
          <div
            className={`px-6 py-8 text-center ${
              isSuccess
                ? "bg-gradient-to-b from-emerald-50 to-white"
                : isFail
                ? "bg-gradient-to-b from-red-50 to-white"
                : "bg-gradient-to-b from-amber-50 to-white"
            }`}
          >
            {isSuccess ? (
              <CheckCircle
                size={56}
                className="mx-auto text-emerald-500 mb-3"
                data-testid="status-icon-success"
              />
            ) : isFail ? (
              <XCircle
                size={56}
                className="mx-auto text-red-400 mb-3"
                data-testid="status-icon-fail"
              />
            ) : (
              <div className="relative mx-auto mb-3 w-14 h-14 flex items-center justify-center">
                <RefreshCw
                  size={56}
                  className="text-amber-400 animate-spin"
                  data-testid="status-icon-pending"
                />
              </div>
            )}

            <h1 className="text-xl font-black text-slate-900">
              {isSuccess
                ? "Payment Successful"
                : isFail
                ? "Payment Failed"
                : "Processing Payment"}
            </h1>
            <p className="text-sm text-slate-500 mt-1">{contextLabel}</p>
          </div>

          {/* Details */}
          <div className="px-6 py-4 space-y-3">
            {/* Simulator label */}
            {isSimulated && (
              <div
                className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                data-testid="simulated-payment-label"
              >
                <AlertTriangle size={13} className="shrink-0 text-amber-600" />
                <span>
                  <strong>SIMULATED PAYMENT</strong> — This is NOT a real
                  gateway transaction. (INV-PAY-SEC-05)
                </span>
              </div>
            )}

            {/* Amount */}
            {intent?.amount && (
              <div className="flex justify-between text-sm border-b border-slate-100 pb-3">
                <span className="text-slate-500">Amount</span>
                <span className="font-bold text-lg">
                  {fmtAmount(intent.amount, intent.currency)}
                </span>
              </div>
            )}

            {/* Receipt */}
            {(receipt?.receipt_number || intent?.receipt_number) && (
              <div
                className="flex items-center justify-between rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3"
                data-testid="receipt-panel"
              >
                <div className="flex items-center gap-2">
                  <Receipt size={16} className="text-emerald-600" />
                  <div>
                    <p className="text-xs text-emerald-600 font-medium">
                      Receipt Number
                    </p>
                    <p className="font-bold text-emerald-800" data-testid="receipt-number">
                      {receipt?.receipt_number || intent?.receipt_number}
                    </p>
                  </div>
                </div>
                {receipt?.issued_at && (
                  <p className="text-xs text-emerald-600">
                    {fmtDateTime(receipt.issued_at)}
                  </p>
                )}
              </div>
            )}

            {/* Intent ID */}
            <div className="flex justify-between text-xs text-slate-500">
              <span>Transaction ID</span>
              <span className="font-mono">{intentId?.slice(0, 20)}…</span>
            </div>

            {/* Payment method */}
            {receipt?.payment_method && (
              <div className="flex justify-between text-xs text-slate-500">
                <span>Method</span>
                <span className="capitalize">{receipt.payment_method}</span>
              </div>
            )}

            {/* Provider */}
            {intent?.provider && (
              <div className="flex justify-between text-xs text-slate-500">
                <span>Provider</span>
                <span
                  className={`font-semibold rounded-full px-2 py-0.5 ${
                    isSimulated
                      ? "bg-amber-100 text-amber-700"
                      : "bg-emerald-100 text-emerald-700"
                  }`}
                >
                  {intent.provider.toUpperCase()}
                </span>
              </div>
            )}

            {/* Polling indicator */}
            {isPending && polling && (
              <div className="flex items-center justify-center gap-2 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                <RefreshCw size={12} className="animate-spin" />
                Checking payment status…
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          {isSuccess && (receipt?.receipt_number || intent?.receipt_number) && (
            <button
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              data-testid="btn-download-pdf"
              className="flex w-full items-center justify-between rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <div className="flex items-center gap-2">
                <Download size={16} className="text-slate-500" />
                {pdfLoading ? "Generating PDF…" : "Download Receipt PDF"}
                {isSimulated && (
                  <span className="ml-1 text-xs font-normal text-amber-600">(Test doc)</span>
                )}
              </div>
              {!pdfLoading && <ChevronRight size={14} className="text-slate-400" />}
            </button>
          )}

          {isSuccess && intent?.fm3_payment_id && (
            <button
              onClick={() =>
                navigate(`/campus/pay/history`)
              }
              data-testid="btn-view-history"
              className="flex w-full items-center justify-between rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <div className="flex items-center gap-2">
                <Receipt size={16} className="text-slate-500" />
                View Payment History
              </div>
              <ChevronRight size={14} className="text-slate-400" />
            </button>
          )}

          {isFail && (
            <button
              onClick={() => navigate(-2)}
              data-testid="btn-retry-payment"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-700"
            >
              <RefreshCw size={15} />
              Retry Payment
            </button>
          )}

          <button
            onClick={() => navigate("/campus/hostel")}
            className="flex w-full items-center justify-between rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
            data-testid="btn-go-home"
          >
            <div className="flex items-center gap-2">
              <Home size={16} className="text-slate-500" />
              Back to Dashboard
            </div>
            <ChevronRight size={14} className="text-slate-400" />
          </button>
        </div>
      </div>
    </div>
  );
}
